import { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Alert,
  Link,
  Tabs,
  Tab,
} from '@mui/material';
import { Google } from '@mui/icons-material';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

// Microsoft icon component
const MicrosoftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
  </svg>
);

export const LoginPage = () => {
  const { loading, signInWithGoogle, signInWithMicrosoft, signInWithEmail, signUpWithEmail, resetPassword, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState(0); // 0 = Sign In, 1 = Sign Up
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);

  // Debug logging
  console.log('[LoginPage] Render - loading:', loading, 'isAuthenticated:', isAuthenticated);

  useEffect(() => {
    console.log('[LoginPage] Auth effect - isAuthenticated:', isAuthenticated);
    if (isAuthenticated) {
      console.log('[LoginPage] Navigating to dashboard');
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    try {
      await signInWithEmail(email, password);
    } catch (err: unknown) {
      const error = err as { code?: string; message?: string };
      const errorCode = error.code || '';
      const errorMessage = error.message || '';

      // Check both code and message for auth errors
      if (
        errorCode.includes('invalid-credential') ||
        errorCode.includes('user-not-found') ||
        errorCode.includes('wrong-password') ||
        errorMessage.includes('invalid-credential')
      ) {
        setLocalError('Invalid email or password');
      } else if (errorCode.includes('too-many-requests') || errorMessage.includes('too-many-requests')) {
        setLocalError('Too many failed attempts. Please try again later.');
      } else if (errorCode.includes('user-disabled') || errorMessage.includes('user-disabled')) {
        setLocalError('This account has been disabled.');
      } else {
        setLocalError('Failed to sign in. Please try again.');
      }
    }
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setLocalError('Password must be at least 6 characters');
      return;
    }
    try {
      await signUpWithEmail(email, password);
    } catch (err: unknown) {
      const error = err as { code?: string; message?: string };
      const errorCode = error.code || '';
      const errorMessage = error.message || '';

      if (errorCode.includes('email-already-in-use') || errorMessage.includes('email-already-in-use')) {
        setLocalError('An account with this email already exists');
      } else if (errorCode.includes('weak-password') || errorMessage.includes('weak-password')) {
        setLocalError('Password is too weak. Please use a stronger password.');
      } else if (errorCode.includes('invalid-email') || errorMessage.includes('invalid-email')) {
        setLocalError('Please enter a valid email address.');
      } else {
        setLocalError('Failed to create account. Please try again.');
      }
    }
  };

  const handleResetPassword = async () => {
    if (!email) {
      setLocalError('Please enter your email address');
      return;
    }
    try {
      await resetPassword(email);
      setResetSent(true);
      setLocalError(null);
    } catch {
      setLocalError('Failed to send reset email. Please check your email address.');
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
        px: 2,
      }}
    >
      <Container maxWidth="sm">
        <Card elevation={2}>
          <CardContent sx={{ textAlign: 'center', py: { xs: 4, md: 6 }, px: { xs: 3, md: 5 } }}>
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{ fontWeight: 700, color: 'primary.main' }}
            >
              Thinkspace
            </Typography>

            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mb: 4, fontWeight: 400 }}
            >
              Collaborative visual thinking for teams
            </Typography>

            {localError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {localError}
              </Alert>
            )}

            {resetSent && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Password reset email sent. Check your inbox.
              </Alert>
            )}

            <Tabs value={tab} onChange={(_, v) => { setTab(v); setLocalError(null); setResetSent(false); }} sx={{ mb: 3 }}>
              <Tab label="Sign In" />
              <Tab label="Sign Up" />
            </Tabs>

            {/* Email/Password Form */}
            <Box component="form" onSubmit={tab === 0 ? handleEmailSignIn : handleEmailSignUp} sx={{ mb: 3 }}>
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  fullWidth
                  size="small"
                />
                {tab === 1 && (
                  <TextField
                    label="Confirm Password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    fullWidth
                    size="small"
                  />
                )}
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  fullWidth
                >
                  {tab === 0 ? 'Sign In' : 'Create Account'}
                </Button>
                {tab === 0 && (
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={handleResetPassword}
                    sx={{ alignSelf: 'center' }}
                  >
                    Forgot password?
                  </Link>
                )}
              </Stack>
            </Box>

            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">or continue with</Typography>
            </Divider>

            <Stack spacing={2} sx={{ maxWidth: 280, mx: 'auto' }}>
              <Button
                variant="outlined"
                size="large"
                onClick={signInWithMicrosoft}
                startIcon={<MicrosoftIcon />}
                disabled={loading}
                fullWidth
              >
                Microsoft
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={signInWithGoogle}
                startIcon={<Google />}
                disabled={loading}
                fullWidth
              >
                Google
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};
